import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { PdfPreviewHost } from "@/components/PdfPreviewHost";
import { BusyLogo } from "@/components/BusyLogo";
import Landing from "@/pages/Landing";
import GoogleCallback from "@/pages/GoogleCallback";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GoogleOneTapAutoLogin } from "@/components/GoogleOneTapAutoLogin";
import PublicActivation from "@/pages/PublicActivation";
import DashboardLayout from "@/components/DashboardLayout";
import { EntityLinksProvider } from "@/components/EntityLink";
import AccountProfile from "@/pages/AccountProfile";
import { PwaInstallProvider } from "@/components/PwaInstall";
import "@/mobile.css";
import "@/App.css";
import { safeLocalStorage as localStorage } from "@/lib/safeStorage";
import IdentityUpload from "@/kyc/pages/IdentityUpload";
import { AdminIdentity } from "@/kyc/components/AdminIdentity";
import Overview from "@/pages/Overview";
import Catalog from "@/pages/Catalog";
import SimActivation from "@/pages/SimActivation";
import Ricariche from "@/pages/Ricariche";
import Customers from "@/pages/Customers";
import Sims from "@/pages/Sims";
import Orders from "@/pages/Orders";
import SimStats from "@/pages/SimStats";
import GiftCards from "@/pages/GiftCards";
import Documents from "@/pages/Documents";
import Compliance from "@/pages/Compliance";
import AdminPlatform from "@/pages/AdminPlatform";
import AdminDealers from "@/pages/AdminDealers";
import AdminDealerUsage from "@/pages/AdminDealerUsage";
import AdminExpiringDocs from "@/pages/AdminExpiringDocs";
import AdminProducts from "@/pages/AdminProducts";
import AdminCategories from "@/pages/AdminCategories";
import AdminReferralPayouts from "@/pages/AdminReferralPayouts";
import AdminIntegrations from "@/pages/AdminIntegrations";
import AdminServicesPanel from "@/pages/AdminServicesPanel";
import InternetWizard from "@/pages/InternetWizard";
import AdminInternetRequests from "@/pages/AdminInternetRequests";
import AdminItaliaMobile from "@/pages/AdminItaliaMobile";
import AdminServicePrices from "@/pages/AdminServicePrices";
import RicaricaCliente from "@/pages/RicaricaCliente";
import BollettiniCliente from "@/pages/BollettiniCliente";
import PaymentResult from "@/pages/PaymentResult";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import EnergyConsent from "@/pages/EnergyConsent";
import GiftClaim from "@/pages/GiftClaim";
import PublicDealers from "@/pages/PublicDealers";
import PublicDealerProfile from "@/pages/PublicDealerProfile";
import DealerProfile from "@/pages/DealerProfile";
import ShipBooking from "@/pages/ShipBooking";
import MobileSell from "@/pages/MobileSell";
import MobileHub from "@/pages/MobileHub";
import AdminMobileOperators from "@/pages/AdminMobileOperators";
import OrderSim from "@/pages/OrderSim";
import DealerItaliaMobile from "@/pages/DealerItaliaMobile";
import ConsoleSim from "@/pages/ConsoleSim";
import WalletHistory from "@/pages/WalletHistory";
import SellEsim from "@/pages/SellEsim";
import SimammaEsim from "@/pages/SimammaEsim";
import { ServiceHistoryPage } from "@/components/ServiceHistoryButton";
import Promotions from "@/pages/Promotions";
import SimInfo from "@/pages/SimInfo";
import SimammaTopup from "@/pages/SimammaTopup";
import OtherOperators from "@/pages/OtherOperators";
import Bollettini from "@/pages/Bollettini";
import OpenApiServices from "@/pages/OpenApiServices";
import AdminEnergyRequests from "@/pages/AdminEnergyRequests";
import AdminEnergyOffers from "@/pages/AdminEnergyOffers";
import GreenPowerSale from "@/pages/GreenPowerSale";
import GreenPowerPratiche from "@/pages/GreenPowerPratiche";
import AdminGreenPower from "@/pages/AdminGreenPower";
import AdminGpCommissions from "@/pages/AdminGpCommissions";
import PlatformDocs from "@/pages/PlatformDocs";
import MyGpCommissions from "@/pages/MyGpCommissions";
import Listino from "@/pages/Listino";
import EnergyTrash from "@/pages/EnergyTrash";
import { EnergySwitch } from "@/components/EnergySwitch";
import AdminMaintenance from "@/pages/AdminMaintenance";
import AdminAudit from "@/pages/AdminAudit";
import ServiziCliente from "@/pages/ServiziCliente";
import GiftCardCliente from "@/pages/GiftCardCliente";import RealEstate from "@/pages/RealEstate";
import Pec from "@/pages/Pec";
import Raccomandata from "@/pages/Raccomandata";
import ReloadlyAirtime from "@/pages/ReloadlyAirtime";
import GiftCardCatalog from "@/pages/GiftCardCatalog";
import Wallet from "@/pages/Wallet";
import PublicOverview from "@/pages/PublicOverview";
import Account from "@/pages/Account";
import MyPurchases from "@/pages/MyPurchases";
import AdminUsers from "@/pages/AdminUsers";
import AdminRegistrations from "@/pages/AdminRegistrations";
import Changelog from "@/pages/Changelog";
import PaymentsHistory from "@/pages/PaymentsHistory";
import ShipmentsConsole from "@/pages/ShipmentsConsole";
import ShippingConfig from "@/pages/ShippingConfig";
import AdminShipServices from "@/pages/AdminShipServices";
import KitAccessori from "@/pages/KitAccessori";
import AddressBook from "@/pages/AddressBook";
import ShippingTerms from "@/pages/ShippingTerms";
import Support from "@/pages/Support";
import MasterDealer from "@/pages/MasterDealer";
import AdminErrorLogs from "@/pages/AdminErrorLogs";
import AdminAccessLogs from "@/pages/AdminAccessLogs";
import AdminEmailLogs from "@/pages/AdminEmailLogs";
import AdminOrdersReport from "@/pages/AdminOrdersReport";
import AdminMargins from "@/pages/AdminMargins";
import AdminOfferVisibility from "@/pages/AdminOfferVisibility";
import HardwareShop from "@/pages/HardwareShop";
import Invoices from "@/pages/Invoices";
import KitMarketing from "@/pages/KitMarketing";
import AgentDealers from "@/pages/AgentDealers";
import AgentInvites from "@/pages/AgentInvites";
import AdminWallet from "@/pages/AdminWallet";
import AgentCommissions from "@/pages/AgentCommissions";
import AdminAgentCommissions from "@/pages/AdminAgentCommissions";
import HelpCenter from "@/pages/HelpCenter";
import AdminAgencies from "@/pages/AdminAgencies";

function AppIndex() {
  const { user } = useAuth();
  if (user?.role === "support") return <Navigate to="assistenza" replace />;
  if (user?.role === "agent") return <Navigate to="agent-dealers" replace />;
  return <Navigate to="dashboard" replace />;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Caricamento…</div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const googleLink = new URLSearchParams(location.search).get("google") === "link";
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Caricamento…</div>;
  if (googleLink) {
    const params = new URLSearchParams(location.search);
    params.delete("google");
    return <Navigate to={{ pathname: "/", search: params.toString(), hash: location.hash }} replace />;
  }
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function ReferralCapture() {
  const { code } = useParams();
  if (code) localStorage.setItem("markoo_ref", code);
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <PwaInstallProvider>
          <GoogleOneTapAutoLogin />
          <Routes>
            <Route path="/" element={<GuestOnly><Landing /></GuestOnly>} />
            <Route path="/overview" element={<PublicOverview />} />
            <Route path="/auth/google" element={<GoogleCallback />} />
            <Route path="/attivazione-simamma/:rid" element={<PublicActivation />} />
            <Route path="/r/:code" element={<ReferralCapture />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/consenso-energia/:token" element={<EnergyConsent />} />
            <Route path="/regalo/:token" element={<GiftClaim />} />
            <Route path="/dealers" element={<PublicDealers />} />
            <Route path="/dealers/:id" element={<PublicDealerProfile />} />
            <Route path="/identity-upload/:token" element={<IdentityUpload />} />
            <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentResult /></Protected>} />
            <Route path="/app" element={<Protected><EntityLinksProvider><DashboardLayout /></EntityLinksProvider></Protected>}>
              <Route index element={<AppIndex />} />
              <Route path="dashboard" element={<Overview />} />
              <Route path="catalogo" element={<Catalog />} />
              <Route path="vendi-sim" element={<MobileSell />} />
              <Route path="mobile/:opKey" element={<MobileHub />} />
              <Route path="operatori-mobili" element={<AdminMobileOperators />} />
              <Route path="ordina-sim" element={<OrderSim />} />
              <Route path="console-sim" element={<ConsoleSim />} />
              <Route path="le-mie-pratiche-sim" element={<DealerItaliaMobile />} />
              <Route path="richieste-italia-mobile" element={<AdminItaliaMobile />} />
              <Route path="prezzi-servizi" element={<AdminServicePrices />} />
              <Route path="ricarica-cliente" element={<RicaricaCliente />} />
              <Route path="bollettini-cliente" element={<BollettiniCliente />} />
              <Route path="vendi-esim" element={<SellEsim />} />
              <Route path="esim-simamma" element={<SimammaEsim />} />
              <Route path="console-sim-simamma" element={<ServiceHistoryPage service="esim" standalone title="Console SIM SIMAMMA" />} />
              <Route path="compliance" element={<Compliance />} />
              <Route path="contratto-piattaforma" element={<AdminPlatform />} />
              <Route path="identita-kyc" element={<AdminIdentity />} />
              <Route path="attiva-promo" element={<Promotions />} />
              <Route path="ricarica-sim" element={<SimammaTopup />} />
              <Route path="info-sim" element={<SimInfo />} />
              <Route path="altri-operatori" element={<OtherOperators />} />
              <Route path="bollettini" element={<Bollettini />} />
              <Route path="servizi-openapi" element={<OpenApiServices />} />
              <Route path="energia" element={<EnergySwitch next={<GreenPowerSale />} />} />
              <Route path="richieste-energia" element={<GreenPowerPratiche />} />
              <Route path="richieste-energia-standby" element={<AdminEnergyRequests />} />
              <Route path="config-energia" element={<AdminGreenPower />} />
              <Route path="provvigioni-energia" element={<AdminGpCommissions />} />
              <Route path="specifiche" element={<PlatformDocs />} />
              <Route path="mie-provvigioni-energia" element={<MyGpCommissions />} />
              <Route path="provvigioni-offerte-energia" element={<Listino />} />
              <Route path="cestino-energia" element={<EnergyTrash />} />
              <Route path="offerte-energia" element={<Navigate to="/app/offerte-rete" replace />} />
              <Route path="manutenzione" element={<AdminMaintenance />} />
              <Route path="valutazioni" element={<RealEstate />} />
              <Route path="pec" element={<Pec />} />
              <Route path="raccomandata" element={<Raccomandata />} />
              <Route path="ricariche-estero" element={<ReloadlyAirtime />} />
              <Route path="catalogo-giftcard" element={<GiftCardCatalog />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="storico-wallet" element={<WalletHistory />} />
              <Route path="account" element={<Account />} />
              <Route path="sicurezza" element={<Account only="sicurezza" />} />
              <Route path="profili/:id" element={<AccountProfile />} />
              <Route path="miei-acquisti" element={<MyPurchases />} />
              <Route path="servizi" element={<ServiziCliente />} />
              <Route path="energia-cliente" element={<EnergySwitch next={<GreenPowerSale />} />} />
              <Route path="giftcard-cliente" element={<GiftCardCliente />} />
              <Route path="novita" element={<Changelog />} />
              <Route path="vetrina" element={<DealerProfile />} />
              <Route path="spedizioni" element={<ShipBooking />} />
              <Route path="pagamenti" element={<PaymentsHistory />} />
              <Route path="console-spedizioni" element={<ShipmentsConsole />} />
              <Route path="config-spedizioni" element={<ShippingConfig />} />
              <Route path="servizi-spedizioni" element={<AdminShipServices />} />
              <Route path="kit-accessori" element={<KitAccessori />} />
              <Route path="rubrica" element={<AddressBook />} />
              <Route path="condizioni-spedizione" element={<ShippingTerms />} />
              <Route path="assistenza" element={<Support />} />
              <Route path="master-dealer" element={<MasterDealer />} />
              <Route path="log-errori" element={<AdminErrorLogs />} />
              <Route path="log-accessi" element={<AdminAccessLogs />} />
              <Route path="email-inviate" element={<AdminEmailLogs />} />
              <Route path="report-ordini" element={<AdminOrdersReport />} />
              <Route path="marginalita" element={<AdminMargins />} />
              <Route path="offerte-rete" element={<AdminOfferVisibility scope="energia" />} />
              <Route path="offerte-rete-servizi" element={<AdminOfferVisibility key="servizi" scope="servizi" />} />
              <Route path="negozio-hardware" element={<HardwareShop />} />
              <Route path="fatture" element={<Invoices />} />
              <Route path="kit-marketing" element={<KitMarketing />} />
              <Route path="agent-dealers" element={<AgentDealers />} />
              <Route path="agent-invites" element={<AgentInvites />} />
              <Route path="attivazione" element={<SimActivation />} />
              <Route path="ricariche" element={<Ricariche />} />
              <Route path="clienti" element={<Customers />} />
              <Route path="sim" element={<Sims />} />
              <Route path="ordini" element={<Orders />} />
              <Route path="statistiche-sim" element={<SimStats />} />
              <Route path="giftcard" element={<GiftCards />} />
              <Route path="documenti" element={<Documents />} />
              <Route path="dealers" element={<AdminDealers />} />
              <Route path="utilizzo-dealer" element={<AdminDealerUsage />} />
              <Route path="documenti-scadenza" element={<AdminExpiringDocs />} />
              <Route path="utenti" element={<AdminUsers />} />
              <Route path="registrazioni-pubbliche" element={<AdminRegistrations />} />
              <Route path="wallet-admin" element={<AdminWallet />} />
              <Route path="audit-crediti" element={<AdminAudit />} />
              <Route path="provvigioni-agenti" element={<AdminAgentCommissions />} />
              <Route path="agent-provvigioni" element={<AgentCommissions />} />
              <Route path="guida" element={<HelpCenter />} />
              <Route path="agenzie" element={<AdminAgencies />} />
              <Route path="prodotti" element={<AdminProducts />} />
              <Route path="categorie" element={<AdminCategories />} />
              <Route path="report" element={<Navigate to="/app/report-ordini" replace />} />
              <Route path="referral-payout" element={<AdminReferralPayouts />} />
              <Route path="integrazioni" element={<AdminIntegrations />} />
              <Route path="config-reloadly" element={<AdminIntegrations section="reloadly" />} />
              <Route path="config-openapi" element={<AdminIntegrations section="openapi" />} />
              <Route path="config-airalo" element={<AdminIntegrations section="airalo" />} />
              <Route path="pannello-servizi" element={<AdminServicesPanel />} />
              <Route path="internet" element={<InternetWizard />} />
              <Route path="richieste-internet" element={<AdminInternetRequests />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </PwaInstallProvider>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
        <PdfPreviewHost />
        <BusyLogo />
        </GoogleOAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
